import {
  DESTINATION_DEFINITION_CATEGORY,
  OBJECT_STORAGE_DEST_DEFINITION_NAMES,
} from '@components/common/constants';
import { getApiErrorMessage } from '@components/common/util/util';
import { IRespData, LiveEvent } from '@components/destinations/liveEvents/interface';
import { constructLiveEvents } from '@components/destinations/liveEvents/util';
import { apiAuthCaller } from '@services/apiCaller';
import { webappClient } from '@lib/bugsnag';
import { IConnectionsStore } from '@stores/connections';
import { IDestinationsListStore, ServerDestination } from '@stores/destinationsList';
import { IMessageStore } from '@stores/messages';
import { ISourcesListStore } from '@stores/sourcesList';
import { ITransformationsListStore } from '@stores/transformationsList';
import { IUserStore } from '@stores/user';
import { IWorkspaceStore } from '@stores/workspace';
import { AxiosResponse } from 'axios';
import { action, computed, observable, makeObservable } from 'mobx';
import { IDestDefinition } from '../destinationDefsList';
import { Transformation } from '../transformation/types';
import { DestinationAccount } from './types';
import {
  DestinationTransformationStore,
  IDestinationTransformation,
  IDestinationTransformationStore,
  ITransformationConfig,
  transformationDestinationDefaultConfig,
} from '@stores/destinationTransformation';
import { ITransformationStore } from '@stores/transformation';
import { IDestinationTransformationList } from '@stores/destinationTransformationList';
import { isDeviceModeSupported } from '@components/transformations/transformationDetails/connections/utils';
import { Analytics } from '@lib/analytics';
import { ISourceStore } from '@stores/source';
import { syncWarehouseDestinationSchema } from '@api';

export enum AuthStatus {
  // TODO: This can be removed as we are looking to put authStatus into destination config
  UNSUPPORTED = 'unsupported',
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

type EventMapping = { from: string; to: string; required?: boolean };

type EventsMapping = {
  destEventName: string;
  rsEventName: string;
  eventProperties: EventMapping[];
};

type CommonMapping = EventMapping[];

export type DestinationConfig = {
  useNativeSDK?: Record<string, boolean>;
  useNativeSDKToSend?: Record<string, boolean>;
  rudderAccountId?: string;
  rudderDeleteAccountId?: string;
  authStatus?: AuthStatus;
  webhookUrl?: string;
  connectionMode?: Record<string, string>;
  commonMapping?: CommonMapping;
  propertiesMapping?: CommonMapping;
  eventsMapping?: EventsMapping[];
  useKeyPairAuth?: boolean;
  [key: string]:
    | string
    | string[]
    | boolean
    | undefined
    | Record<string, unknown>
    | CommonMapping
    | EventsMapping[];
};
export type SecretConfig = Record<string, unknown>;

/**
 * Advisory the read attaches when a destination's stored major is not the definition's current one.
 * Absent on a current-version destination — absence is the "no advisory" signal; there is no
 * `action: none` on the wire.
 */
export type VersionInfo = {
  status: 'supported' | 'retired';
  action: 'upgrade_recommended' | 'upgrade_required';
  retirementDate?: string;
  migrationDocsUrl?: string;
};

/**
 * Read-only response of `POST /destinations/<id>/migrate`: the stored config forward-migrated to the
 * definition's current major, split exactly like a read (masked secrets in `secretConfig`, never in
 * `config`), plus the validation failures the migrated shape has against the target schema. Persists
 * nothing — the commit is a separate write.
 */
export type MigrationPreview = {
  version: number;
  config: DestinationConfig;
  secretConfig: SecretConfig;
  validationErrors: { field: string; message: string }[];
};

export interface IDestination {
  id: string;
  name: string;
  enabled: boolean;
  config: DestinationConfig;
  secretConfig: SecretConfig;
  destinationDefinition: IDestDefinition;
  /** The definition major this destination is pinned to. Always present on a read. */
  version: number;
  versionInfo?: VersionInfo;
  sources: ISourceStore[];
  transformationConnections: IDestinationTransformationStore[];
  transformations: ITransformationStore[];
  destinationTransformations: IDestinationTransformation[];
  isEventStreamSourceConnected: boolean;
  isRetlSourceConnected: boolean;
}

export interface IDestinationStore extends IDestination {
  setName(name: string): void;
  toggleEnabled(config?: DestinationConfig): Promise<void>;
  updateConfig(
    config: DestinationConfig,
    hardUpdate?: boolean,
    onSuccess?: () => void,
    onError?: () => void,
  ): Promise<boolean>;
  previewMigration(): Promise<MigrationPreview | undefined>;
  upgradeToVersion(version: number, config: DestinationConfig): Promise<boolean>;
  updateName(name: string): Promise<boolean>;
  getLiveEvents(latestEventId: number, from: number): Promise<LiveEvent[]>;
  isMapped(): boolean;
  isWarehouse(): boolean;
  isObjectStorage(): boolean;
  setConfig(data: { config: DestinationConfig; secretConfig: SecretConfig }): void;
  getAccounts(): Promise<DestinationAccount[]>;
  deleteAccount(accountId: string, onSuccess: () => void): void;
  changeActiveAccount(
    account: { accountId: string; accountIdKey: string },
    onSuccess: () => void,
  ): void;
  getTransformationConnection(
    transformationId: string,
  ): IDestinationTransformationStore | undefined;
  syncWarehouseDestinationSchema(): Promise<boolean>;
}

interface IUpdateDestinationBody {
  enabled: boolean;
  config?: DestinationConfig;
}

type RootStore = {
  connectionsStore: IConnectionsStore;
  destinationsListStore: IDestinationsListStore;
  transformationsListStore: ITransformationsListStore;
  workspaceStore: IWorkspaceStore;
  sourcesListStore: ISourcesListStore;
  userStore: IUserStore;
  messagesStore: IMessageStore;
  destinationTransformationsListStore: IDestinationTransformationList;
};

export class DestinationStore implements IDestinationStore {
  @observable public id: string;

  @observable public name: string;

  @observable public enabled: boolean;

  @observable public config: DestinationConfig;

  @observable public secretConfig: SecretConfig;

  @observable public destinationDefinition: IDestDefinition;

  @observable public version: number;

  @observable public versionInfo?: VersionInfo;

  @observable public destinationTransformations: IDestinationTransformation[];

  @observable public rootStore: RootStore;

  constructor(destination: Omit<ServerDestination, 'permissions'>, rootStore: RootStore) {
    makeObservable(this);
    this.id = destination.id;
    this.name = destination.name;
    this.enabled = destination.enabled;
    this.config = destination.config;
    this.secretConfig = destination.secretConfig;
    this.destinationDefinition = destination.destinationDefinition;
    this.version = destination.version;
    this.versionInfo = destination.versionInfo;
    this.rootStore = rootStore;
    this.destinationTransformations =
      destination?.destinationTransformations?.map(
        (dt) => new DestinationTransformationStore(dt, rootStore),
      ) || [];
    this.addTrasformationConnectionsToStore();
  }

  @action.bound
  public setName(name: string): void {
    this.name = name;
  }

  @computed get sources() {
    const sourceIds: string[] = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const key in this.rootStore.connectionsStore.connections) {
      if (this.rootStore.connectionsStore.connections[key].includes(this.id)) {
        sourceIds.push(key);
      }
    }
    return this.rootStore.sourcesListStore.sources.filter((source) =>
      sourceIds.includes(source.id),
    );
  }

  @computed get transformations() {
    return this.rootStore.transformationsListStore.transformations.filter(
      (transformation: Transformation) =>
        transformation.destinationIds && transformation.destinationIds.includes(this.id),
    );
  }

  private addTrasformationConnectionsToStore() {
    const connections: IDestinationTransformation[] = this.transformations.map((trans) => {
      const destinationTransformation = this.destinationTransformations?.find(
        (dm) => dm.transformationId === trans.id,
      );
      return (
        destinationTransformation || {
          destinationId: this.id,
          transformationId: trans.id,
          config: transformationDestinationDefaultConfig,
        }
      );
    });

    this.rootStore.destinationTransformationsListStore.add(connections);
  }

  @computed get transformationConnections() {
    return this.rootStore.destinationTransformationsListStore.getByDestinationId(this.id);
  }

  @action.bound
  /**
   * @param {object} config - The configuration for the destination
   * This is a more generic object and varies from destination to destination
   * For OAuth supported destinations, there will be a field `rudderAccountId` which indicates
   * that the destination has been authorised by the account with this id.
   *
   * This object contains a property `authStatus` whose explanation is provided below
   * If the destination has been disable due to an exception such revoking of an account,
   * then when the user tries to enable the destination he/she will be asked to re-authorise.
   * After re-auth is successful we would need to change the authStatus from INACTIVE to
   * ACTIVE explicitly. This parameter indicates the authorisation status for a destination
   */
  public async toggleEnabled(config?: DestinationConfig) {
    const { messagesStore } = this.rootStore;

    try {
      if (
        this.enabled === false &&
        (!this.config || Object.keys(this.config).length === 0) &&
        (!this.secretConfig || Object.keys(this.secretConfig).length === 0)
      ) {
        messagesStore.showErrorMessage('Please update your destination settings to enable');
        return;
      }

      const updateBody = { enabled: !this.enabled, config };

      await apiAuthCaller().post<IUpdateDestinationBody>(`/destinations/${this.id}`, updateBody);
      this.enabled = !this.enabled;
      if (config) {
        this.config = config;
      }

      messagesStore.showSuccessMessage(
        `destination ${this.enabled ? 'enabled' : 'disabled'} successfully`,
      );

      if (!this.enabled) {
        Analytics.track({
          event: 'destinationDisabled',
          properties: {
            destinationId: this.id,
            destinationType: this.destinationDefinition.name,
          },
        });
      }
    } catch (error) {
      messagesStore.showErrorMessage(
        getApiErrorMessage(error, `Failed to ${this.enabled ? 'disable' : 'enable'} destination`),
      );
    }
  }

  @action.bound
  public async updateConfig(
    config: DestinationConfig,
    hardUpdate = false,
    onSuccess: () => void = () => null,
    onError: () => void = () => null,
  ) {
    const { messagesStore } = this.rootStore;
    try {
      const updateUrl = `/destinations/${this.id}${hardUpdate ? '?hardUpdate=true' : ''}`;
      // No `version` in the body: the server keeps the destination on its stored major when the
      // field is omitted, and this method is the shared write behind the settings form, the
      // connect-a-source flows and the rETL propagation — pinning it would gate all of them on a
      // version check they never opted into, while the sibling writers (toggleEnabled,
      // updateRegionConfig, the audience-sync editDestination) stay unpinned anyway. Flow C sends
      // an explicit target major on the one write that migrates, with conflict handling.
      const res = await apiAuthCaller().post<ServerDestination>(updateUrl, {
        config,
      });
      this.applyWriteResponse(res.data);
      onSuccess();

      this.resetTransformationConnectionsIfDeviceModeUnsupported();

      return true;
    } catch (error) {
      onError();
      messagesStore.showErrorMessage(
        getApiErrorMessage(error, 'Failed to save destination configuration'),
      );
      return false;
    }
  }

  /**
   * Adopt the state a write response re-serves. The embedded `destinationDefinition` is the slice for
   * the destination's *resolved* major, so taking it back is what re-renders the config form on the
   * major an upgrade just landed on — without a refetch. For a same-major edit it is the slice the
   * model already holds, so the assignment is a no-op.
   */
  @action
  private applyWriteResponse(data: ServerDestination) {
    this.config = data.config;
    this.secretConfig = data.secretConfig;
    // Only adopted when the major actually moved. Every write to this endpoint answers with a
    // definition, but on a same-major edit it is the slice we already hold, so replacing it buys
    // nothing and would let any response that ever trims the definition quietly degrade the form for
    // the dozen unrelated callers of updateConfig (OAuth re-auth, account linking, source connect).
    const hasMovedMajor = typeof data.version === 'number' && data.version !== this.version;
    if (hasMovedMajor && data.destinationDefinition) {
      this.destinationDefinition = data.destinationDefinition;
    }
    this.version = data.version;
    this.versionInfo = data.versionInfo;
  }

  /**
   * Read-only preview of the upgrade to the definition's current major. The endpoint resolves the
   * target itself — there is no version to send — and persists nothing, so this is safe to call
   * before the user has committed to anything.
   */
  @action.bound
  public async previewMigration() {
    const { messagesStore } = this.rootStore;
    try {
      const res = await apiAuthCaller().post<MigrationPreview>(`/destinations/${this.id}/migrate`);
      const preview = res.data;
      // The response is typed but not trusted: the form dereferences every one of these, and a
      // response missing `config` or `validationErrors` would take the whole destination page down
      // to a blank render rather than failing here where it can be reported and recovered from.
      if (!preview || typeof preview.version !== 'number' || !preview.config) {
        webappClient.notify(
          new Error(`Migration preview for destination ${this.id} returned an unusable shape`),
        );
        messagesStore.showErrorMessage('Failed to prepare the destination upgrade');
        return undefined;
      }
      return {
        ...preview,
        secretConfig: preview.secretConfig ?? {},
        validationErrors: preview.validationErrors ?? [],
      };
    } catch (error) {
      // A version failure (a missing migration, a retired target) answers with a readable message of
      // its own, which getApiErrorMessage appends — no client-side code-to-copy table to drift.
      messagesStore.showErrorMessage(
        getApiErrorMessage(error, 'Failed to prepare the destination upgrade'),
      );
      return undefined;
    }
  }

  /**
   * Commit the upgrade: the one write that pins an explicit `version`. The server forward-migrates the
   * stored config, overlays this config, validates against the target major and persists it. Kept
   * apart from `updateConfig` — which stays deliberately unpinned so the flows that never opted into a
   * version check (source connect, rETL propagation, OAuth re-auth) cannot be gated by one.
   */
  @action.bound
  public async upgradeToVersion(version: number, config: DestinationConfig) {
    const { messagesStore } = this.rootStore;
    try {
      const res = await apiAuthCaller().post<ServerDestination>(`/destinations/${this.id}`, {
        version,
        config,
      });
      this.applyWriteResponse(res.data);
      // Same cleanup `updateConfig` performs after any config write. An upgrade is MORE likely to
      // need it, not less: the migration chain can rename or drop the `useNativeSDK` keys this is
      // derived from, so device-mode support can flip on exactly this write.
      this.resetTransformationConnectionsIfDeviceModeUnsupported();
      return true;
    } catch (error) {
      messagesStore.showErrorMessage(
        getApiErrorMessage(error, 'Failed to upgrade the destination'),
      );
      return false;
    }
  }

  /**
   * Device-mode transformation connections carry settings that only mean anything while the
   * destination actually runs in device mode. Once it does not, they are reset to the default rather
   * than left behind as stale config no screen would explain.
   */
  private resetTransformationConnectionsIfDeviceModeUnsupported() {
    // Best effort, and deliberately unable to fail the write it follows: the config is already
    // persisted by this point, so letting a problem here surface as a failed save would tell the
    // user their change did not land when it did.
    try {
      if (!isDeviceModeSupported(this)) {
        Promise.all(
          this?.transformationConnections?.map((transformation) => transformation.resetConfig()),
        ).catch((error) => webappClient.notify(error as Error));
      }
    } catch (error) {
      webappClient.notify(error as Error);
    }
  }

  @action.bound
  /**
   * updateName
   */
  public async updateName(name: string) {
    const { messagesStore } = this.rootStore;

    try {
      const updateUrl = `/destinations/${this.id}/updateName`;
      const res = await apiAuthCaller().post(updateUrl, {
        name,
      });
      this.name = res.data.name;
      return true;
    } catch (error) {
      messagesStore.showErrorMessage(getApiErrorMessage(error, 'Failed to update name'));
      return false;
    }
  }

  @action.bound
  public async disconnectTransformations() {
    try {
      const { destinationTransformationsListStore } = this.rootStore;
      await apiAuthCaller().post(`/destinations/${this.id}/disconnectAllTransformations`);
      destinationTransformationsListStore.removeByDestinationId(this.id);
    } catch (error) {}
  }

  public async getLiveEvents(latestEventId: number, timestamp: number) {
    try {
      const resp = await apiAuthCaller().get<IRespData[]>(`/eventDeliveryStatus`, {
        headers: { 'X-DESTINATION-ID': this.id },
        params: { id: latestEventId, from: timestamp },
      });
      return constructLiveEvents(resp.data);
    } catch (err) {
      return [];
    }
  }

  public isMapped() {
    return this.sources.some(
      (source) =>
        source.config?.constants?.some(
          (obj: { key: string; value: string }) =>
            obj.key === 'context.mappedToDestination' && obj.value,
        ),
    );
  }

  public isWarehouse() {
    return this.destinationDefinition.category === DESTINATION_DEFINITION_CATEGORY.WAREHOUSE;
  }

  public isObjectStorage() {
    return OBJECT_STORAGE_DEST_DEFINITION_NAMES.includes(this.destinationDefinition.name);
  }

  @action.bound
  public setConfig(data: { config: DestinationConfig; secretConfig: SecretConfig }) {
    this.config = data.config;
    this.secretConfig = data.secretConfig;
  }

  @action.bound
  async getAccounts() {
    const response: AxiosResponse<DestinationAccount[]> = await apiAuthCaller().get(
      `/destination/accounts/${
        this.destinationDefinition.config?.auth?.role ||
        this.destinationDefinition.name.toLowerCase()
      }`,
    );
    return response.data;
  }

  @action.bound
  async deleteAccount(accountId: string, onSuccess: () => void) {
    const { messagesStore } = this.rootStore;
    try {
      await apiAuthCaller().ignore400().delete(`/destination/accounts/${accountId}`);
      onSuccess();
    } catch (err) {
      messagesStore.showErrorMessage(getApiErrorMessage(err, 'Failed to delete account'));
    }
  }

  @action.bound
  async changeActiveAccount(
    account: { accountId: string; accountIdKey: string },
    onSuccess: () => void,
  ) {
    const config = {
      ...this.config,
      authStatus: AuthStatus.ACTIVE,
      [account.accountIdKey]: account.accountId,
    };
    await this.updateConfig(config, false, onSuccess);
  }

  public async updateTransformationConnectionSettings(
    transformationId: string,
    config: ITransformationConfig,
  ) {
    const destinationTransformation = this.getTransformationConnection(transformationId);
    await destinationTransformation?.update(config);
  }

  getTransformationConnection(
    transformationId: string,
  ): IDestinationTransformationStore | undefined {
    return this.rootStore.destinationTransformationsListStore.get(this.id, transformationId);
  }

  @action.bound
  async syncWarehouseDestinationSchema() {
    const { messagesStore } = this.rootStore;

    try {
      await syncWarehouseDestinationSchema(this.id);
      messagesStore.showSuccessMessage('Schema synced successfully');
      return true;
    } catch (err) {
      messagesStore.showErrorMessage(err as string);
      return false;
    }
  }

  @computed
  get isEventStreamSourceConnected() {
    return this.sources.some((source) => source.isEventStream());
  }

  @computed
  get isRetlSourceConnected() {
    return this.sources.some((source) => source.category === 'warehouse');
  }
}
